import * as React from 'react';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';

export type OTPInputProps = {
  code: string;
  onChange: (code: string) => void;
  numFields: number;
  isError?: boolean;
  isDisabled?: boolean;
};

const codeInNumber = (code: string, numFields: number): string => {
  return code.replace(/\D/g, '').slice(0, numFields);
};

const otpPlaceholderText = (valueLength: number, maxLength: number): string => {
  return ' '.repeat(valueLength) + '_'.repeat(maxLength - valueLength);
};

/** This component should most likely be used as part of 2FA within `@dx-ui/osc-two-factor-auth`. */
export const OTPInput: React.FC<React.PropsWithChildren<OTPInputProps>> = ({
  code,
  onChange,
  numFields,
  isError,
  isDisabled,
}) => {
  const { t, ready } = useTranslation('osc-otp-input');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const otpCode = codeInNumber(code, numFields);
  const hasError = Boolean(isError && otpCode.length < numFields);

  const setValue = React.useCallback(
    (value: string) => {
      const newCode = value.replace(/\D/g, '').slice(0, numFields);
      onChange(newCode);
    },
    [numFields, onChange]
  );

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();

  const onInput = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setValue(e.currentTarget.value);
    },
    [setValue]
  );

  const onKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Space') {
      e.preventDefault();
      return;
    }
  }, []);

  const onPaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, numFields);
      if (text) {
        setValue(text);
      }
    },
    [setValue, numFields]
  );

  React.useEffect(() => {
    if (isError && !isDisabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isError, isDisabled, otpCode.length, numFields]);

  if (!ready || isNaN(numFields)) {
    return null;
  }

  const isComplete = otpCode.length === numFields;
  const placeholderText = otpPlaceholderText(isComplete ? 0 : otpCode.length, numFields);

  return (
    <div className="inline-grid w-min">
      <input
        ref={inputRef}
        aria-invalid={hasError}
        aria-label={t('inputLabel')}
        aria-required="true"
        autoCapitalize="off"
        autoComplete="one-time-code"
        autoCorrect="off"
        className={cx(
          'heading--xl form-input col-start-1 row-start-1 flex h-16 w-full items-center justify-center rounded-md border-2 bg-transparent pe-2 ps-2 text-start font-mono font-medium tracking-[0.25em]',
          {
            'border-2 !border-surface-interactive-hover': otpCode.length,
            'form-error': hasError,
          }
        )}
        disabled={isDisabled}
        inputMode="numeric"
        maxLength={numFields}
        pattern={`\\d{${numFields}}`}
        spellCheck={false}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onInput={onInput}
        onPaste={onPaste}
        type="tel"
        value={otpCode}
      />
      <div
        aria-hidden="true"
        className={cx(
          'heading--xl pointer-events-none col-start-1 row-start-1 flex h-16 w-full items-center justify-start whitespace-pre pe-2 ps-3 text-start font-mono font-medium tracking-[0.25em] opacity-50',
          { invisible: isComplete }
        )}
      >
        {placeholderText}
      </div>
    </div>
  );
};

export default OTPInput;
